import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@ui-nuvertos/shared';

@Injectable({
  providedIn: 'root'
})
export class AssetTransferService {
  private assetUrl = environment.assetUrl;

  constructor(private http: HttpClient) { }

  getTransfers(filters?: any): Observable<any> {
    const url = `${this.assetUrl}/api/v1/asset-transfers/`;
    let params = new HttpParams();
    if (filters) {
      if (filters.limit) params = params.set('limit', filters.limit.toString());
      if (filters.offset !== undefined) params = params.set('offset', filters.offset.toString());
      if (filters.status) params = params.set('status', filters.status);
      if (filters.source_subsidiary_id) params = params.set('source_subsidiary_id', filters.source_subsidiary_id);
      if (filters.destination_subsidiary_id) params = params.set('destination_subsidiary_id', filters.destination_subsidiary_id);
      if (filters.asset_id) params = params.set('asset_id', filters.asset_id);
      if (filters.asset_request_id) params = params.set('asset_request_id', filters.asset_request_id);
      if (filters.get_references) params = params.set('get_references', filters.get_references);
      if (filters.search) params = params.set('search', filters.search);
    }
    return this.http.get<any>(url, { params, withCredentials: true });
  }

  getTransfer(id: string): Observable<any> {
    const url = `${this.assetUrl}/api/v1/asset-transfers/${id}`;
    return this.http.get<any>(url, { withCredentials: true });
  }

  approveTransfer(id: string, body?: any): Observable<any> {
    const url = `${this.assetUrl}/api/v1/asset-transfers/${id}/approve`;
    return this.http.post<any>(url, body || {}, { withCredentials: true });
  }

  rejectTransfer(id: string, body?: any): Observable<any> {
    const url = `${this.assetUrl}/api/v1/asset-transfers/${id}/reject`;
    return this.http.post<any>(url, body || {}, { withCredentials: true });
  }

  dispatchTransfer(id: string, body?: any): Observable<any> {
    const url = `${this.assetUrl}/api/v1/asset-transfers/${id}/dispatch`;
    return this.http.post<any>(url, body || {}, { withCredentials: true });
  }

  receiveTransfer(id: string, body?: any): Observable<any> {
    const url = `${this.assetUrl}/api/v1/asset-transfers/${id}/receive`;
    return this.http.post<any>(url, body || {}, { withCredentials: true });
  }

  deleteTransfer(id: string): Observable<any> {
    const url = `${this.assetUrl}/api/v1/asset-transfers/${id}`;
    return this.http.delete<any>(url, { withCredentials: true });
  }

  createTransfer(data: any): Observable<any> {
    const url = `${this.assetUrl}/api/v1/asset-transfers/`;
    return this.http.post<any>(url, data, { withCredentials: true });
  }

  updateTransfer(id: string, data: any): Observable<any> {
    const url = `${this.assetUrl}/api/v1/asset-transfers/${id}`;
    return this.http.put<any>(url, data, { withCredentials: true });
  }

  // Form Template APIs
  getFormTemplates(): Observable<any> {
    const url = `${this.assetUrl}/api/v1/asset-transfer-form-templates/`;
    return this.http.get<any>(url, { withCredentials: true });
  }

  getFormTemplateById(id: string): Observable<any> {
    const url = `${this.assetUrl}/api/v1/asset-transfer-form-templates/${id}`;
    return this.http.get<any>(url, { withCredentials: true });
  }

  getDefaultFormTemplate(): Observable<any> {
    const url = `${this.assetUrl}/api/v1/asset-transfer-form-templates/default`;
    return this.http.get<any>(url, { withCredentials: true });
  }

  // Transfer Asset Form Template APIs (New)
  getTransferAssetFormTemplates(): Observable<any> {
    const url = `${this.assetUrl}/api/v1/transfer-asset-form-templates/`;
    return this.http.get<any>(url, { withCredentials: true });
  }

  getTransferAssetFormTemplateById(id: string): Observable<any> {
    const url = `${this.assetUrl}/api/v1/transfer-asset-form-templates/${id}`;
    return this.http.get<any>(url, { withCredentials: true });
  }

  getDefaultTransferAssetFormTemplate(): Observable<any> {
    const url = `${this.assetUrl}/api/v1/transfer-asset-form-templates/default`;
    return this.http.get<any>(url, { withCredentials: true });
  }
}

/**
 * Whether a transfer may be dispatched.
 *
 * Kept deliberately in step with the server (AssetTransferService.DispatchTransfer),
 * which updates only when:
 *   status = 'approved'  OR  (status = 'pending' AND approval_status = 'not_required')
 *
 * `not_required` covers tenants with no approval workflow configured for
 * asset_transfer - those keep the original straight-to-dispatch behaviour.
 */
export function isTransferDispatchable(raw: any): boolean {
  if (!raw) return false;
  if (raw.status === 'approved') return true;
  return raw.status === 'pending' && raw.approval_status === 'not_required';
}
